import { Button, Card, Form, Input, InputNumber, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Concession = {
  id: string;
  demand_id: string;
  amount: number;
  reason: string;
  status: string;
  approval_instance_id?: string | null;
  credit_note_id?: string | null;
  concession_type?: string | null;
  period_key?: string;
  student_id?: string;
  created_at?: string;
};

export default function FeeConcessionsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canConcession = Boolean(
    user?.permissions?.includes('education.fee.concession') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-fee-concessions'],
    queryFn: () => api.get<Concession[]>('/education/fees/concessions'),
  });

  const submit = useMutation({
    mutationFn: (values: { demand_id: string; amount: number; reason: string; concession_type?: string }) =>
      api.post('/education/fees/concessions', values),
    onSuccess: () => {
      message.success(t('feeConcessionSubmitted'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-concessions'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feeConcessions')}
      description={t('feeConcessionsIntro')}
      extra={<Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={query.isLoading}
            dataSource={query.data?.data ?? []}
            pagination={{ pageSize: 20 }}
            columns={[
              { title: t('periodKey'), dataIndex: 'period_key', width: 110 },
              { title: t('amount'), dataIndex: 'amount', width: 100 },
              {
                title: t('concessionType', { defaultValue: 'Type' }),
                dataIndex: 'concession_type',
                width: 120,
                render: (v?: string | null) => v || '—',
              },
              { title: t('reason'), dataIndex: 'reason', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('creditNote'),
                dataIndex: 'credit_note_id',
                ellipsis: true,
                render: (v) => v || '—',
              },
            ]}
          />
        </Card>
        {canConcession ? (
          <Card size="small" title={t('submitFeeConcession')}>
            <Form layout="vertical" style={{ maxWidth: 520 }} onFinish={(values) => submit.mutate(values)}>
              <Form.Item name="demand_id" label={t('feeDemand')} rules={[{ required: true }]}>
                <Input placeholder={t('demandIdHint')} />
              </Form.Item>
              <Form.Item name="amount" label={t('amount')} rules={[{ required: true }]}>
                <InputNumber min={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="concession_type" label={t('concessionType', { defaultValue: 'Type' })}>
                <Select
                  allowClear
                  options={['sibling', 'staff', 'scholarship', 'waiver', 'other'].map((value) => ({ value, label: value }))}
                />
              </Form.Item>
              <Form.Item name="reason" label={t('reason')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submit.isPending}>
                {t('submitForApproval')}
              </Button>
            </Form>
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
